import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import AnimatedSection from "./AnimatedSection";
import avatar1 from "../../../images/avatar/img01.jpg";
import avatar2 from "../../../images/avatar/img02.jpg";
import avatar3 from "../../../images/avatar/img03.jpg";
import avatar4 from "../../../images/avatar/img04.jpg";

interface Testimonial {
  id: number;
  name: string;
  role: string;
  rating: number;
  review: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Rahul Sharma",
    role: "Director, Retail Solutions",
    rating: 5,
    review:
      "WebTech delivered a fast, mobile-friendly website that perfectly matched our brand. Affordable pricing and on-time delivery — highly recommended.",
    avatar: avatar1,
  },
  {
    id: 2,
    name: "Priya Nair",
    role: "Founder, StyleCart",
    rating: 5,
    review:
      "Our e-commerce store went live within a week. The admin panel is easy to use and the team provided excellent post-launch support.",
    avatar: avatar2,
  },
  {
    id: 3,
    name: "Vikram Reddy",
    role: "Marketing Head, TechServe",
    rating: 5,
    review:
      "Professional web redesign that modernized our outdated site. Traffic and enquiries increased noticeably within the first month of launch.",
    avatar: avatar3,
  },
  {
    id: 4,
    name: "Anita Desai",
    role: "Owner, Wellness Hub",
    rating: 5,
    review:
      "End-to-end service from design to hosting and SEO. WebTech understood our business and built a website that speaks clearly to our customers.",
    avatar: avatar4,
  },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="wdev-testimonial__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 20 20" aria-hidden="true">
        <path
          d="M10 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 15.8 4.2 17.9l.9-5.4L1.2 7.7l5.4-.8L10 2Z"
          fill={i < rating ? "#fbbf24" : "rgba(255,255,255,0.15)"}
        />
      </svg>
    ))}
  </div>
);

const WdevTestimonials: React.FC = () => {
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <section className="wdev-testimonials" aria-labelledby="wdev-testimonials-title">
      <div className="container">
        <AnimatedSection className="wdev-section-head wdev-section-head--center">
          <h2 id="wdev-testimonials-title" className="wdev-section-head__title">
            What Our Clients Say
          </h2>
        </AnimatedSection>
      </div>

      <AnimatedSection className="wdev-testimonials__slider-wrap" delay={0.1}>
        <button
          ref={prevRef}
          type="button"
          className="wdev-testimonials__nav wdev-testimonials__nav--prev"
          aria-label="Previous testimonial"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <Swiper
          modules={[Autoplay, Pagination, Navigation]}
          loop
          speed={600}
          spaceBetween={24}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={{
            1200: { slidesPerView: 3 },
            768: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          className="wdev-testimonials__slider"
        >
          {testimonials.map((item) => (
            <SwiperSlide key={item.id}>
              <article className="wdev-testimonial-card">
                <StarRating rating={item.rating} />
                <p className="wdev-testimonial-card__review">&ldquo;{item.review}&rdquo;</p>
                <div className="wdev-testimonial-card__author">
                  <img src={item.avatar} alt="" className="wdev-testimonial-card__avatar" />
                  <div>
                    <h3 className="wdev-testimonial-card__name">{item.name}</h3>
                    <span className="wdev-testimonial-card__role">{item.role}</span>
                  </div>
                </div>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          ref={nextRef}
          type="button"
          className="wdev-testimonials__nav wdev-testimonials__nav--next"
          aria-label="Next testimonial"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </AnimatedSection>
    </section>
  );
};

export default WdevTestimonials;
